<script lang="ts" setup>
import { computed, ref, PropType } from "vue";
import { Modal, ProgressSpinner } from "@/components";
import { useForm, useField } from "vee-validate";
import { string } from "yup";
import { useI18n } from "vue-i18n";
import { HTTP } from "@/util/http";
import { token } from "@/init";
import { useStore } from "vuex";
import { WorkspaceDocumentFragment } from "@/graphql/operations";

const store = useStore();

const BASE_URL = import.meta.env.VUE_APP_BACKEND_URL || "/backend";

const props = defineProps({
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: false,
  },
});

const emit = defineEmits(["close", "success"]);

const { t } = useI18n();

const { validate, errors } = useForm();
const normalizeUrl = (value: string) => {
  if (!value) return value;
  return value.match(/^https?:\/\//i) ? value : `https://${value}`;
};

const { value: url, resetField: resetUrl } = useField<string>(
  "url",
  string()
    .transform((value) => {
      return normalizeUrl(value);
    })
    .url(t("urlBasedDocumentImport.url.error.invalid"))
    .required(t("urlBasedDocumentImport.url.error.missing")),
);

const { value: externalId, resetField: resetExternalId } = useField<string>(
  "externalId",
  string()
    .min(1, t("urlBasedDocumentImport.externalId.error.missing"))
    .required(t("urlBasedDocumentImport.externalId.error.missing")),
);

const loading = ref(false);
const errorMessage = ref<string | null>(null);
const sendNotification = ref(true);
const showUnbrandedClassification = computed(() =>
  store.getters.user.hasUnbrandedDocumentClassification(),
);
const isUnbranded = ref(props.doc?.isUnbranded ?? false);

const submit = async () => {
  const validationResult = await validate();
  if (!validationResult.valid) {
    return;
  }

  errorMessage.value = null;
  loading.value = true;

  try {
    const normalizedUrl = normalizeUrl(url.value);

    const headers = new Map();
    headers.set("Authorization", `Bearer ${token.value}`);

    const fetchUrl = BASE_URL + "/url_based_document_import";
    const data = {
      url: normalizedUrl,
      externalId: externalId.value,
      workspaceId: props.doc?.workspaceId ?? store.getters.currentWorkspace.id,
      documentId: props.doc?.id,
      sendNotification: sendNotification.value,
      ...(showUnbrandedClassification.value
        ? { isUnbranded: isUnbranded.value }
        : {}),
    };

    const response = await HTTP.post(fetchUrl, headers, JSON.stringify(data));

    // Give the upload service a moment to sync to Firestore
    await new Promise((resolve) => setTimeout(resolve, 1500));

    emit("success");
    emit("close");
  } catch (error: any) {
    loading.value = false;

    // Handle specific error codes
    if (error.status === 400) {
      errorMessage.value = t("urlBasedDocumentImport.error.badRequest");
    } else if (error.status === 401) {
      errorMessage.value = t("urlBasedDocumentImport.error.unauthorized");
    } else if (error.status >= 400 && error.status < 600) {
      errorMessage.value = t("urlBasedDocumentImport.error.serverError");
    } else {
      errorMessage.value = t("urlBasedDocumentImport.error.generic");
    }
  }
};

const onInvalidSubmit = () => {
  // Form validation already handles displaying errors
};
</script>

<template>
  <Modal
    :active="true"
    @close="emit('close')"
    :closeDisabled="loading === true"
  >
    <template #header>
      {{ $t("urlBasedDocumentImport.title") }}
    </template>

    <template #body>
      <div v-if="loading" class="loading_state">
        <progress-spinner />
        <p>{{ $t("urlBasedDocumentImport.loading") }}</p>
      </div>
      <div v-else>
        <div v-if="errorMessage" class="message error">
          {{ errorMessage }}
        </div>

        <form
          ref="form"
          @submit.prevent="submit"
          @invalid-submit="onInvalidSubmit"
        >
          <div class="field">
            <label>{{ $t("urlBasedDocumentImport.url.label") }}</label>
            <input
              name="url"
              :class="{ input: true, error: errors.url !== undefined }"
              v-model="url"
              type="text"
              placeholder="https://www.pitchview.io"
            />
            <p v-if="errors.url" class="input error">{{ errors.url }}</p>
          </div>

          <div class="field">
            <label>{{ $t("urlBasedDocumentImport.externalId.label") }}</label>
            <input
              name="externalId"
              :class="{ input: true, error: errors.externalId !== undefined }"
              v-model="externalId"
              type="text"
              :placeholder="$t('urlBasedDocumentImport.externalId.placeholder')"
            />
            <p v-if="errors.externalId" class="input error">
              {{ errors.externalId }}
            </p>
          </div>

          <div v-if="showUnbrandedClassification" class="checkbox_container">
            <input
              id="url-import-unbranded"
              v-model="isUnbranded"
              type="checkbox"
              :disabled="loading"
            />
            <label for="url-import-unbranded">
              {{ $t("pvDocEditor.info.unbranded.label") }}
            </label>
          </div>
        </form>
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <div class="checkbox_container">
          <input
            type="checkbox"
            id="send-notification"
            v-model="sendNotification"
            :disabled="loading"
          />
          <label for="send-notification">
            {{ $t("upload.sendNotificationLabel") }}
          </label>
        </div>
        <button
          class="button primary"
          :class="{ disabled: loading }"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.window) {
  max-width: calc(100vw - 24px);
}

.field {
  margin-bottom: 1.5em;

  label {
    display: block;
    margin-bottom: 0.5em;
    font-weight: 500;
    font-size: 0.9em;
  }
}

.loading_state {
  text-align: center;
  padding: 2em 0;

  p {
    margin-top: 1em;
    color: var(--classic-text-secondary, rgba(25, 23, 17, 0.6));
  }
}

.message.error {
  margin-bottom: 1.5em;
}

.checkbox_container {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.8em;
  color: var(--classic-text-secondary, rgba(25, 23, 17, 0.8));
}

.checkbox_container label {
  margin: 0;
}
</style>
